import React, { useCallback } from 'react';
import { Card } from '@totalsoft/rocket-ui';
import ListAltIcon from '@mui/icons-material/ListAlt';
import { PROPERTY_LIST_QUERY } from '../queries';
import { APPLICATIONS_QUERY } from 'features/properties/components/edit/queries';
import { PROPERTY_DELETED } from '../subscriptions';
import { useToast } from 'hooks/toasts/useToast';
import { useTranslation } from 'react-i18next';
import { CollectionType, getListBrowserStore } from '@ts-private/list-browser';
import { ExportModalComponent } from 'components/common/export/ExportModalComponent';
import { useMemo } from 'react';
import TableListBrowser from 'features/common/components/table-list-browser/TableListBrowser';
import { useMutation } from 'hooks/apollo/useMutation';
import { DELETE_PROPERTY } from '../mutations';
import useConfirmDialog from 'features/common/components/confirm/useConfirmDialog';
import PropertyItem from './PropertyItem';
import ApolloFetchingComponent from 'features/common/components/apollo-fetching/ApolloFetchingComponent';
import useSubscription from 'hooks/apollo/useSubscription';

export const propertiesStore = getListBrowserStore({
  filterDefinitions: [
    {
      name: 'name',
      caption: 'Property.List.Row.Name',
      type: 'text'
    },
    {
      name: 'code',
      caption: 'Property.List.Row.Code',
      type: 'text'
    },
    {
      name: 'appCode',
      caption: 'Property.List.Row.AppCode',
      type: 'select',
      underlyingType: 'string'
    }
  ],
  orderByFields: [
    {
      caption: 'Property.List.Row.Name',
      value: 'PropertyName'
    },
    {
      caption: 'Property.List.Row.Code',
      value: 'PropertyCode'
    }
  ],
  pager: {
    orderBy: 'PropertyName',
    direction: 'asc'
  }
});

const PropertyListBrowser = () => {
  const [t] = useTranslation();
  const addToast = useToast();
  const confirm = useConfirmDialog();

  const fetchingOptions = useMemo(
    () => ({
      queryOptions: {
        query: PROPERTY_LIST_QUERY,
        name: 'pagedProperties'
      },
      collectionsOptions: [
        {
          queryOptions: { query: APPLICATIONS_QUERY },
          name: 'applicationsConfig',
          identifier: 'appCode',
          collectionType: CollectionType.Filter,
          valueKey: 'appCode',
          labelKey: 'appName'
        }
      ]
    }),
    []
  );

  // #region Delete

  useSubscription(PROPERTY_DELETED, {
    onData: ({ client }) => {
      addToast(t('Property.PropertyDeleted'), 'success');
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'pagedProperties' });
      client.cache.gc();
    }
  });

  const [deleteProperty] = useMutation(DELETE_PROPERTY);

  const handleDelete = useCallback(
    propertyId => {
      confirm(
        () => {
          deleteProperty({ variables: { propertyId } });
        },
        {
          content: t('Property.DeleteProperty')
        }
      );
    },
    [deleteProperty, confirm, t]
  );

  // #endregion

  const itemComponent = useCallback(({ data }) => <PropertyItem data={data} onDelete={handleDelete} />, [handleDelete]);

  const columns = useMemo(
    () => [
      { name: 'name', caption: 'Property.List.Row.Name' },
      { name: 'code', caption: 'Property.List.Row.Code' },
      { name: 'type', caption: 'Property.List.Row.Type' },
      { name: 'actions', caption: 'Actions', width: '120px' }
    ],
    []
  );

  return (
    <Card icon={ListAltIcon}>
      <TableListBrowser name='properties-browser' store={propertiesStore} ItemComponent={itemComponent} columns={columns} />
      <ExportModalComponent store={propertiesStore} options={fetchingOptions} />
      <ApolloFetchingComponent store={propertiesStore} options={fetchingOptions} />
    </Card>
  );
};

export default PropertyListBrowser;
